import type { CatalogParamModel } from '@/shared/generated'
import type { ZodType } from 'zod'

import z from 'zod'

import i18n from '@/shared/i18n/client'

import { aggregationOptionsFor } from './helpers'

const requiredMsg = () =>
	i18n.t('validation_required', { defaultValue: 'Required' })

/** Base validator for a param, ignoring required/optional. */
const baseSchemaFor = (param: CatalogParamModel): ZodType => {
	switch (param.type) {
		case 'integer':
			return z.preprocess((val: unknown) => {
				if (val === '' || val == null) return undefined
				if (typeof val === 'string') return Number.parseInt(val, 10)

				return val
			}, z.int()) //z.coerce.number().int()
		case 'decimal':
			return z.preprocess((val: unknown) => {
				if (val === '' || val == null) return undefined
				if (typeof val === 'string') return Number.parseFloat(val)

				return val
			}, z.number()) //z.coerce.number()
		case 'boolean':
			return z.boolean()
		case 'enum':
			return param.options?.length
				? z.enum(param.options as [string, ...string[]])
				: z.string()
		case 'aggregation_type':
			return z.enum(aggregationOptionsFor(param) as [string, ...string[]])
		case 'competitor_list':
			return z.array(z.string())
		case 'price_column':
			return z.string()
		case 'string':
		default:
			return z.string()
	}
}

/** Validator for a single param, applying required/optional semantics. */
export const fieldSchemaFor = (param: CatalogParamModel): ZodType => {
	const base = baseSchemaFor(param)

	if (!param.required) return base.optional()

	if (param.type === 'competitor_list')
		return (base as z.ZodArray<z.ZodString>).min(1, requiredMsg())

	if (base instanceof z.ZodString) return base.min(1, requiredMsg())

	return base
}

/**
 * Build a Zod object schema from a variant's params, keyed by `param.id`.
 * Drives the campaign params form's `zodResolver`.
 */
export const buildParamsSchema = (params: CatalogParamModel[]) =>
	z.object(
		Object.fromEntries(params.map((param) => [param.id, fieldSchemaFor(param)]))
	)
